/**
 * กฎการตีบวกอุปกรณ์ (+1 ถึง +10) ตาม GDD หัวข้อ 5
 *
 * อยู่ใน shared เพราะทั้งเซิร์ฟเวอร์และไคลเอนต์ต้องคำนวณและแสดงค่าตรงกัน
 * เซิร์ฟเวอร์ใช้ตัดสินโอกาสสำเร็จและการหักของ ไคลเอนต์ใช้แสดงโอกาสและค่าสเตตัสล่วงหน้า
 *
 * ทุกฟังก์ชันในไฟล์นี้เป็น pure function ไม่สุ่มและไม่อ่าน state (ยกเว้น rollRefine ที่รับค่าสุ่มเข้ามา)
 */
/** ระดับตีบวกสูงสุดตาม GDD */
export const MAX_REFINE_LEVEL = 10;
/** หินตีบวกแยกตามประเภทอุปกรณ์ */
export const REFINE_STONE_ARMOR = "refine_stone_armor";
export const REFINE_STONE_WEAPON = "refine_stone_weapon";
export const REFINE_STONE_WINGS = "refine_stone_wings";
export const REFINE_STONE_ACCESSORY = "refine_stone_accessory";
export const REFINE_STONE_SPECIAL = "refine_stone_special";
/** หินตีบวกเริ่มต้น/เดิม */
export const REFINE_STONE_ID = "refine_stone";
/** หินป้องกันการลดระดับเมื่อล้มเหลว */
export const PROTECT_STONE_ID = "protect_stone";
/** รายการหินตีบวกทั้งหมดในเกม */
export const ALL_REFINE_STONES = Object.freeze([
    REFINE_STONE_ARMOR,
    REFINE_STONE_WEAPON,
    REFINE_STONE_WINGS,
    REFINE_STONE_ACCESSORY,
    REFINE_STONE_SPECIAL,
    REFINE_STONE_ID,
]);
/** ตรวจว่าเป็นหินตีบวกหรือไม่ */
export function isRefineStone(id) {
    return ALL_REFINE_STONES.includes(id);
}
/** ตรวจว่าเป็นไอเทมเกี่ยวกับระบบตีบวก (หินตีบวกหรือหินคุ้มครอง) หรือไม่ */
export function isRefineItem(id) {
    return isRefineStone(id) || id === PROTECT_STONE_ID;
}
/**
 * ช่องที่อนุญาตให้ตีบวกได้
 * - ชุด: head, face, body, legs, feet
 * - อาวุธและโล่: weapon, offhand
 * - ปีก: wings
 * - ประดับ: ear1, ear2
 * - ไอเทมพิเศษ: special
 */
export const REFINEABLE_SLOTS = Object.freeze([
    "weapon",
    "offhand",
    "head",
    "face",
    "body",
    "legs",
    "feet",
    "wings",
    "ear1",
    "ear2",
    "special",
    "pet",
]);
/**
 * ระบุหินตีบวกเฉพาะที่ต้องใช้สำหรับแต่ละช่องอุปกรณ์
 * 1. หิน+ชุด (เสื้อ กางเกง รองเท้า หมวก หน้ากาก)
 * 2. หิน+อาวุธและโล่ (มือหลัก, มือรอง)
 * 3. หิน+ปีก
 * 4. หิน+ประดับ (ต่างหู)
 * 5. หิน+ไอเทมพิเศษ / สัตว์เลี้ยง
 */
export function stoneForSlot(slot) {
    switch (slot) {
        case "head":
        case "face":
        case "body":
        case "legs":
        case "feet":
            return REFINE_STONE_ARMOR;
        case "weapon":
        case "offhand":
            return REFINE_STONE_WEAPON;
        case "wings":
            return REFINE_STONE_WINGS;
        case "ear1":
        case "ear2":
            return REFINE_STONE_ACCESSORY;
        case "special":
        case "pet":
            return REFINE_STONE_SPECIAL;
        default:
            return undefined;
    }
}
/** ตรวจว่าช่องนี้สามารถตีบวกได้หรือไม่ */
export function canRefineSlot(slot) {
    return stoneForSlot(slot) !== undefined;
}
/**
 * ตารางโอกาสสำเร็จและบทลงโทษเมื่อล้มเหลวตาม GDD หัวข้อ 5:
 * +1 ถึง +4: 100%, ล้มเหลวไม่ลดระดับ
 * +5: 80%, ล้มเหลวไม่ลดระดับ (เสียแค่วัตถุดิบ)
 * +6: 60%, ล้มเหลวลด 1 ระดับ
 * +7: 40%, ล้มเหลวลด 1 ระดับ
 * +8: 25%, ล้มเหลวลด 2 ระดับ
 * +9 ถึง +10: 10%, ล้มเหลวลด 2 ระดับ
 */
export const REFINE_RULES = Object.freeze({
    1: { chance: 1.0, failPenalty: 0 },
    2: { chance: 1.0, failPenalty: 0 },
    3: { chance: 1.0, failPenalty: 0 },
    4: { chance: 1.0, failPenalty: 0 },
    5: { chance: 0.80, failPenalty: 0 },
    6: { chance: 0.60, failPenalty: 1 },
    7: { chance: 0.40, failPenalty: 1 },
    8: { chance: 0.25, failPenalty: 2 },
    9: { chance: 0.10, failPenalty: 2 },
    10: { chance: 0.10, failPenalty: 2 },
});
/** ดึงกติกาสำหรับระดับเป้าหมาย (targetLevel: 1..10) */
export function getRefineRule(targetLevel) {
    const rule = REFINE_RULES[targetLevel];
    if (!rule) {
        throw new RangeError(`ระดับตีบวกไม่ถูกต้อง: ${targetLevel} (รองรับ 1–${MAX_REFINE_LEVEL})`);
    }
    return rule;
}
/**
 * ค่าธรรมเนียมทองในการตีบวกแต่ละระดับ (Gold sink ตาม GDD หัวข้อ 8)
 * คิดตามระดับเป้าหมาย:
 * +1: 100g, +2: 200g, +3: 400g, +4: 700g, +5: 1100g,
 * +6: 1600g, +7: 2200g, +8: 3000g, +9: 4000g, +10: 5500g
 */
export function refineGoldCost(targetLevel) {
    if (targetLevel <= 0)
        return 0;
    if (targetLevel === 1)
        return 100;
    if (targetLevel === 2)
        return 200;
    if (targetLevel === 3)
        return 400;
    if (targetLevel === 4)
        return 700;
    if (targetLevel === 5)
        return 1100;
    if (targetLevel === 6)
        return 1600;
    if (targetLevel === 7)
        return 2200;
    if (targetLevel === 8)
        return 3000;
    if (targetLevel === 9)
        return 4000;
    return 5500;
}
/**
 * ค่าพลังที่เพิ่มขึ้นจากการตีบวกตาม GDD:
 * "ทุกระดับเพิ่ม ATK/DEF ประมาณ 5–8% ของค่าพื้นฐาน" (ใช้ 6% ต่อระดับ)
 * ถ้าค่าพื้นฐาน > 0 จะได้อย่างน้อย 1 หน่วยต่อระดับเสมอ
 */
export function refineBonus(baseValue, level) {
    if (baseValue <= 0 || level <= 0)
        return 0;
    const pctBonus = Math.round(baseValue * 0.06 * level);
    return Math.max(level, pctBonus);
}
/**
 * ทำการทอยเต๋าตีบวก (pure calculation)
 *
 * @param currentLevel ระดับปัจจุบัน (0 ถึง MAX_REFINE_LEVEL - 1)
 * @param useProtect ใช้หินคุ้มครองหรือไม่
 * @param roll เลขสุ่ม 0.0 - 1.0 (ฝั่งเซิร์ฟเวอร์ส่ง Math.random())
 */
export function rollRefine(currentLevel, useProtect, roll) {
    if (currentLevel < 0 || currentLevel >= MAX_REFINE_LEVEL) {
        throw new RangeError(`ไม่สามารถตีบวกจากระดับ ${currentLevel} ได้`);
    }
    const targetLevel = currentLevel + 1;
    const rule = getRefineRule(targetLevel);
    const success = roll < rule.chance;
    if (success) {
        return {
            success: true,
            oldLevel: currentLevel,
            newLevel: targetLevel,
            levelChange: 1,
            protected: false,
        };
    }
    // ล้มเหลว
    let penalty = rule.failPenalty;
    let wasProtected = false;
    if (penalty > 0 && useProtect) {
        penalty = 0;
        wasProtected = true;
    }
    const newLevel = Math.max(0, currentLevel - penalty);
    return {
        success: false,
        oldLevel: currentLevel,
        newLevel,
        levelChange: newLevel - currentLevel,
        protected: wasProtected,
    };
}
//# sourceMappingURL=refine.js.map